import { type ScoreResultMeetingRef, type UserScoreResultRow } from '../../shared/insights/store/insights'

export type AreaCategory = 'WORKFLOW' | 'ENGAGEMENT' | 'SCHEDULING'

export interface AreaToImprove {
  category: AreaCategory
  title: string
  tip: string
  flaggedCount: number
  totalMeetings: number
  trend: 'fewer' | 'more' | 'steady' | 'new'
  trendMagnitude: number
  meetings: ScoreResultMeetingRef[]
}

interface CriterionMeta {
  category: AreaCategory
  title: string
  tip: string
  nullCount: (row: UserScoreResultRow) => number
  previousNullCount: (row: UserScoreResultRow) => number
  meetings: (row: UserScoreResultRow) => ScoreResultMeetingRef[]
}

// Every scorecard criterion the daily result chart tracks, in the same grouping as the per-meeting scorecard
const CRITERIA: CriterionMeta[] = [
  {
    category: 'WORKFLOW',
    title: 'Next steps discussed',
    tip: 'Clearly state next steps and confirm the client understands them before ending the call.',
    nullCount: (row) => row.wf_nextStepsImprovementCount,
    previousNullCount: (row) => row.wf_nextStepsPreviousImprovementCount,
    meetings: (row) => row.wf_nextStepsImprovementSessions,
  },
  {
    category: 'WORKFLOW',
    title: 'Life events discussed',
    tip: 'Ask about recent life changes — job, family, health — that could affect their plan.',
    nullCount: (row) => row.wf_lifeEventsImprovementCount,
    previousNullCount: (row) => row.wf_lifeEventsPreviousImprovementCount,
    meetings: (row) => row.wf_lifeEventsImprovementSessions,
  },
  {
    category: 'WORKFLOW',
    title: 'Follow-up tasks collected',
    tip: 'Capture concrete follow-up tasks and note an owner before the meeting ends.',
    nullCount: (row) => row.wf_followUpTasksImprovementCount,
    previousNullCount: (row) => row.wf_followUpTasksPreviousImprovementCount,
    meetings: (row) => row.wf_followUpTasksImprovementSessions,
  },
  {
    category: 'WORKFLOW',
    title: 'Agenda presented',
    tip: 'Open the meeting by walking through today’s agenda.',
    nullCount: (row) => row.wf_meetingAgendaImprovementCount,
    previousNullCount: (row) => row.wf_meetingAgendaPreviousImprovementCount,
    meetings: (row) => row.wf_meetingAgendaImprovementSessions,
  },
  {
    category: 'WORKFLOW',
    title: 'Core motivators covered',
    tip: 'Touch on the client’s core motivators — dreams, fears, goals — or your account’s custom theme.',
    nullCount: (row) => row.wf_coreMotivatorsImprovementCount,
    previousNullCount: (row) => row.wf_coreMotivatorsPreviousImprovementCount,
    meetings: (row) => row.wf_coreMotivatorsImprovementSessions,
  },
  {
    category: 'WORKFLOW',
    title: 'Decisions agreed',
    tip: 'Drive toward at least one concrete decision the client agrees to.',
    nullCount: (row) => row.wf_decisionsAgreedImprovementCount,
    previousNullCount: (row) => row.wf_decisionsAgreedPreviousImprovementCount,
    meetings: (row) => row.wf_decisionsAgreedImprovementSessions,
  },
  {
    category: 'WORKFLOW',
    title: 'Consensus confirmed',
    tip: 'Address disagreement in the moment rather than leaving it unresolved.',
    nullCount: (row) => row.wf_decisionsNotAgreedImprovementCount,
    previousNullCount: (row) => row.wf_decisionsNotAgreedPreviousImprovementCount,
    meetings: (row) => row.wf_decisionsNotAgreedImprovementSessions,
  },
  {
    category: 'ENGAGEMENT',
    title: 'Client sentiment',
    tip: 'Watch for signs of frustration or hesitation and address them directly.',
    nullCount: (row) => row.eng_sentimentImprovementCount,
    previousNullCount: (row) => row.eng_sentimentPreviousImprovementCount,
    meetings: (row) => row.eng_sentimentImprovementSessions,
  },
  {
    category: 'ENGAGEMENT',
    title: 'Client talk time',
    tip: 'Ask open-ended questions so the client speaks at least 30% of the meeting.',
    nullCount: (row) => row.eng_avgTalkTimeImprovementCount,
    previousNullCount: (row) => row.eng_avgTalkTimePreviousImprovementCount,
    meetings: (row) => row.eng_avgTalkTimeImprovementSessions,
  },
  {
    category: 'ENGAGEMENT',
    title: 'Question rate',
    tip: 'Ask questions consistently through every phase of the meeting, not just the start.',
    nullCount: (row) => row.eng_questionRateImprovementCount,
    previousNullCount: (row) => row.eng_questionRatePreviousImprovementCount,
    meetings: (row) => row.eng_questionRateImprovementSessions,
  },
  {
    category: 'ENGAGEMENT',
    title: 'Client questions invited',
    tip: 'Pause after key topics and ask if the client has any questions.',
    nullCount: (row) => row.eng_clientQuestionsImprovementCount,
    previousNullCount: (row) => row.eng_clientQuestionsPreviousImprovementCount,
    meetings: (row) => row.eng_clientQuestionsImprovementSessions,
  },
  {
    category: 'ENGAGEMENT',
    title: 'Client connection',
    tip: 'Cover rapport-building topics — or your account’s custom theme — early in the call.',
    nullCount: (row) => row.eng_clientConnectionImprovementCount,
    previousNullCount: (row) => row.eng_clientConnectionPreviousImprovementCount,
    meetings: (row) => row.eng_clientConnectionImprovementSessions,
  },
  {
    category: 'ENGAGEMENT',
    title: 'Personal engagement',
    tip: 'Touch on personal topics like family and hobbies — or your account’s custom theme.',
    nullCount: (row) => row.eng_personalEngagementImprovementCount,
    previousNullCount: (row) => row.eng_personalEngagementPreviousImprovementCount,
    meetings: (row) => row.eng_personalEngagementImprovementSessions,
  },
  {
    category: 'ENGAGEMENT',
    title: 'Balanced conversation',
    tip: 'Avoid long uninterrupted monologues; check in with the client regularly.',
    nullCount: (row) => row.eng_avoidedLongMonologueImprovementCount,
    previousNullCount: (row) => row.eng_avoidedLongMonologuePreviousImprovementCount,
    meetings: (row) => row.eng_avoidedLongMonologueImprovementSessions,
  },
  {
    category: 'ENGAGEMENT',
    title: 'Interactivity',
    tip: 'Maintain a consistent back-and-forth across the entire meeting.',
    nullCount: (row) => row.eng_interactiveConversationImprovementCount,
    previousNullCount: (row) => row.eng_interactiveConversationPreviousImprovementCount,
    meetings: (row) => row.eng_interactiveConversationImprovementSessions,
  },
  {
    category: 'SCHEDULING',
    title: 'Meeting booked in advance',
    tip: 'Book meetings in advance with a calendar invite instead of ad hoc calls.',
    nullCount: (row) => row.sch_calendarEventImprovementCount,
    previousNullCount: (row) => row.sch_calendarEventPreviousImprovementCount,
    meetings: (row) => row.sch_calendarEventImprovementSessions,
  },
  {
    category: 'SCHEDULING',
    title: 'Time allocation',
    tip: 'Stay within about 10% of the scheduled meeting length.',
    nullCount: (row) => row.sch_overUnderTimeImprovementCount,
    previousNullCount: (row) => row.sch_overUnderTimePreviousImprovementCount,
    meetings: (row) => row.sch_overUnderTimeImprovementSessions,
  },
  {
    category: 'SCHEDULING',
    title: 'Follow-up meeting booked',
    tip: 'Book the next session before the call ends, with a specific date and time.',
    nullCount: (row) => row.sch_futureMeetingsImprovementCount,
    previousNullCount: (row) => row.sch_futureMeetingsPreviousImprovementCount,
    meetings: (row) => row.sch_futureMeetingsImprovementSessions,
  },
]

const sumNullCount = (rows: UserScoreResultRow[], meta: CriterionMeta): number => rows.reduce((sum, row) => sum + (meta.nullCount(row) || 0), 0)

const sumPreviousNullCount = (rows: UserScoreResultRow[], meta: CriterionMeta): number => rows.reduce((sum, row) => sum + (meta.previousNullCount(row) || 0), 0)

// Most recent meeting first; same-day meetings break ties by score ascending (worst first)
const compareMeetings = (a: ScoreResultMeetingRef, b: ScoreResultMeetingRef): number => (a.date === b.date ? a.score - b.score : a.date < b.date ? 1 : -1)

const collectMeetings = (rows: UserScoreResultRow[], meta: CriterionMeta): ScoreResultMeetingRef[] => {
  const seen = new Set<string>()
  const meetings: ScoreResultMeetingRef[] = []
  rows.forEach((row) => {
    meta.meetings(row).forEach((ref) => {
      const key = `${ref.room_id}:${ref.session_id}`
      if (!seen.has(key)) {
        seen.add(key)
        meetings.push(ref)
      }
    })
  })
  return meetings.sort(compareMeetings)
}

// Highest score first; same-score meetings break ties by most recent
const compareByScoreDescending = (a: ScoreResultMeetingRef, b: ScoreResultMeetingRef): number =>
  a.score === b.score ? (a.date < b.date ? 1 : a.date > b.date ? -1 : 0) : b.score - a.score

export const TOP_MEETINGS_CAP = 5

export const selectTopMeetings = (rows: UserScoreResultRow[], limit: number = TOP_MEETINGS_CAP): ScoreResultMeetingRef[] => {
  const seen = new Set<string>()
  const meetings: ScoreResultMeetingRef[] = []
  rows.forEach((row) => {
    ;(row.top_meetings ?? []).forEach((ref) => {
      const key = `${ref.room_id}:${ref.session_id}`
      if (!seen.has(key)) {
        seen.add(key)
        meetings.push(ref)
      }
    })
  })
  return meetings.sort(compareByScoreDescending).slice(0, limit)
}

export const buildAreasToImprove = (rows: UserScoreResultRow[], totalMeetings: number): AreaToImprove[] =>
  CRITERIA.map((meta) => ({ meta, flaggedCount: sumNullCount(rows, meta) }))
    .filter(({ flaggedCount }) => flaggedCount > 0)
    .map(({ meta, flaggedCount }): AreaToImprove => {
      const previousCount = sumPreviousNullCount(rows, meta)
      const trend: AreaToImprove['trend'] =
        previousCount === 0 ? 'new' : flaggedCount === previousCount ? 'steady' : flaggedCount > previousCount ? 'more' : 'fewer'
      return {
        category: meta.category,
        title: meta.title,
        tip: meta.tip,
        flaggedCount,
        totalMeetings,
        trend,
        trendMagnitude: Math.abs(flaggedCount - previousCount),
        meetings: collectMeetings(rows, meta),
      }
    })
    .sort((a, b) => b.flaggedCount - a.flaggedCount)

// Floor for an area to be worth focusing on: flagged in at least this share of scored meetings
// AND at least this many times. Both must hold. Neither value is calibrated against real data yet.
export const AREA_MIN_FLAG_RATE = 0.1
export const AREA_MIN_FLAG_COUNT = 3

// Rows shown before the rest collapse behind a "Show all" link
export const AREA_LIST_CAP = 5

// Below this many flagged areas there is nothing worth offering to reveal. Not calibrated yet either.
export const SCATTERED_MIN_FLAGGED_AREAS = 3

// ranked: areas clear the floor — scattered: flags exist but none often enough — sparse: barely any flags at all
export type AreasToImproveState = 'ranked' | 'scattered' | 'sparse'

export interface AreasToImproveList {
  state: AreasToImproveState
  // Areas the card may show, ranked by flag count descending
  areas: AreaToImprove[]
  flaggedAreaCount: number
}

export const clearsAreaFloor = (area: AreaToImprove, totalMeetings: number): boolean =>
  area.flaggedCount >= AREA_MIN_FLAG_COUNT && area.flaggedCount >= totalMeetings * AREA_MIN_FLAG_RATE

export const selectAreasToImprove = (areas: AreaToImprove[], totalMeetings: number): AreasToImproveList => {
  const focusAreas = areas.filter((area) => clearsAreaFloor(area, totalMeetings))
  if (focusAreas.length > 0) {
    return { state: 'ranked', areas: focusAreas, flaggedAreaCount: areas.length }
  }
  return { state: areas.length >= SCATTERED_MIN_FLAGGED_AREAS ? 'scattered' : 'sparse', areas, flaggedAreaCount: areas.length }
}
